import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { IconName } from '../../../../../components/Atoms/Icon/Icon.enum';
import sendGTMEvent from '../../../../common/analytics/sendGTMEvent';
import { MIN_CHARACTERS, SEARCH_MAGNET_PHRASES } from '../../../../common/constants/search';
import { AutocompleteItem } from '../AutocompleteItem/AutocompleteItem';
import { SuggestionsWrapper } from './Suggestions.styled';
export const Suggestions = ({ phrase, activeSuggestionIndex, setActiveSuggestionIndex, setIsOpen, handleClick, setPhrase, suggestions, isMobileOrTablet, }) => {
    const [preparedSuggestions, setPreparedSuggestions] = useState([]);
    const wrapperRef = useRef(null);
    const isLastSuggestionItem = (suggestionList, activeIndex) => {
        if (!suggestionList.length)
            return false;
        return suggestionList.length - 1 === activeIndex;
    };
    const handleSuggestionSelect = useCallback(({ index }) => {
        var _a, _b;
        const selectedSuggestedPhrase = ((_a = preparedSuggestions[activeSuggestionIndex]) === null || _a === void 0 ? void 0 : _a.phrase) || null;
        const selectedSuggestedCategories = ((_b = preparedSuggestions[activeSuggestionIndex]) === null || _b === void 0 ? void 0 : _b.categories) || null;
        const selectedPhrase = selectedSuggestedPhrase || phrase;
        const queryParams = selectedSuggestedCategories && selectedSuggestedCategories.length
            ? { category: selectedSuggestedCategories[0] }
            : undefined;
        if (isLastSuggestionItem(preparedSuggestions, activeSuggestionIndex)) {
            sendGTMEvent({
                event: 'search_perform',
                search_term: selectedPhrase && selectedPhrase.toLowerCase(),
                search_input: phrase && phrase.toLowerCase(),
                search_method: 'all_results|link',
                search_category: '',
            });
        }
        else {
            sendGTMEvent({
                event: 'search_perform',
                search_term: selectedPhrase && selectedPhrase.toLowerCase(),
                search_input: phrase && phrase.toLowerCase(),
                search_method: `autocomplete|${(queryParams === null || queryParams === void 0 ? void 0 : queryParams.category) ? 'category' : 'regular'}`,
                search_category: (queryParams === null || queryParams === void 0 ? void 0 : queryParams.category) || '',
            });
        }
        setPhrase(phrase);
        handleClick &&
            handleClick({
                searchPhrase: phrase,
                params: queryParams,
                category: selectedSuggestedCategories && selectedSuggestedCategories.length
                    ? selectedSuggestedCategories[0]
                    : '',
                listingFunction: 'suggestions',
                listingType: 'autocomplete',
                phrase: selectedPhrase,
                searchPosition: index || 0,
            });
    }, [preparedSuggestions, activeSuggestionIndex, phrase, handleClick, setPhrase]);
    const preparedMagnetSuggestions = useMemo(() => SEARCH_MAGNET_PHRASES.filter((magnetPhrase) => { var _a; return magnetPhrase.includes((_a = phrase === null || phrase === void 0 ? void 0 : phrase.toLowerCase()) !== null && _a !== void 0 ? _a : ''); }).map((magnet) => ({ phrase: magnet, categories: ['accessories'] })), [phrase]);
    useEffect(() => {
        if (!phrase || phrase.length <= MIN_CHARACTERS)
            return;
        const filteredMagnetPhrases = SEARCH_MAGNET_PHRASES.filter((element) => element.includes(phrase.toLowerCase()))
            ? preparedMagnetSuggestions
            : [];
        const filteredSuggestions = suggestions
            ? suggestions
                .map((suggestion, index) => {
                var _a;
                const isShowCategory = [0, 1].includes(index);
                if (isShowCategory && ((_a = suggestion === null || suggestion === void 0 ? void 0 : suggestion.categories) === null || _a === void 0 ? void 0 : _a.length)) {
                    return {
                        phrase: suggestion.phrase,
                        categories: [suggestion.categories[0]],
                    };
                }
                return suggestion;
            })
                .flatMap((suggestion) => {
                var _a;
                return ((_a = suggestion === null || suggestion === void 0 ? void 0 : suggestion.categories) === null || _a === void 0 ? void 0 : _a.length)
                    ? [{ phrase: suggestion.phrase }, suggestion]
                    : [suggestion];
            })
            : [];
        const suggestionBundle = [...filteredMagnetPhrases, ...filteredSuggestions]
            .slice(0, isMobileOrTablet ? 8 : 10)
            .concat({ phrase });
        setPreparedSuggestions(suggestionBundle);
    }, [setPreparedSuggestions, preparedMagnetSuggestions, suggestions, isMobileOrTablet, phrase]);
    const handleKeyboardEvents = useCallback((event) => {
        const { keyCode } = event;
        const suggestionsLength = preparedSuggestions ? preparedSuggestions.length - 1 : 0;
        const phraseLength = phrase === null || phrase === void 0 ? void 0 : phrase.length;
        switch (keyCode) {
            case 8:
                phraseLength && phraseLength - 1 <= MIN_CHARACTERS && setPreparedSuggestions([]);
                break;
            case 46:
                setActiveSuggestionIndex(-1);
                break;
            case 13:
                if (activeSuggestionIndex !== -1) {
                    event.preventDefault();
                    phrase && handleSuggestionSelect({ index: activeSuggestionIndex });
                }
                break;
            case 27:
                setIsOpen(false);
                break;
            case 38:
                event.preventDefault();
                setActiveSuggestionIndex(activeSuggestionIndex > 0 ? activeSuggestionIndex - 1 : suggestionsLength);
                break;
            case 40:
                event.preventDefault();
                setActiveSuggestionIndex(activeSuggestionIndex < suggestionsLength ? activeSuggestionIndex + 1 : 0);
                break;
            default:
        }
    }, [
        preparedSuggestions,
        phrase,
        setActiveSuggestionIndex,
        handleSuggestionSelect,
        setIsOpen,
        activeSuggestionIndex,
    ]);
    useEffect(() => {
        window.addEventListener('keydown', handleKeyboardEvents);
        return () => {
            window.removeEventListener('keydown', handleKeyboardEvents);
        };
    }, [handleKeyboardEvents]);
    return (preparedSuggestions === null || preparedSuggestions === void 0 ? void 0 : preparedSuggestions.length) && (phrase === null || phrase === void 0 ? void 0 : phrase.length) > 2 ? (_jsx(SuggestionsWrapper, Object.assign({ ref: wrapperRef, "data-testid": "main-search-input-suggestions-panel" }, { children: preparedSuggestions.map((suggestion, index) => (_jsx(AutocompleteItem, { activeSuggestionIndex: 0, "data-testid": `main-search-input-suggestion-${index}`, iconName: IconName.DISPLATE_SEARCH_AUTOCOMPLETE, isActive: activeSuggestionIndex === index, isLastItem: preparedSuggestions.length - 1 === index, onClick: (e) => {
                e.stopPropagation();
                handleSuggestionSelect({ index });
            }, onMouseEnter: () => setActiveSuggestionIndex(index), phrase: phrase, setActiveSuggestionIndex: setActiveSuggestionIndex, suggestion: suggestion, tabIndex: index }, `_${suggestion.phrase}-${index}`))) }), void 0)) : null;
};
//# sourceMappingURL=Suggestions.js.map